/* LEGAL-NOTICE
   © Copyright 2018 - Quid Informatica S.p.A. - All Rights Reserved
   https://quidinfo.it/qinetic/licence.html - "Quid Consumer Lending Platform"
   Deposito SIAE n. 012702 del 26 Luglio 2018
LEGAL-NOTICE */
import { Injectable } from '@angular/core';
import {
  ErrorDisplay,
  ErrorButtonModel,
  NotificationSkipModel
} from '@core/models/error';
import { ToastController, ModalController } from '@ionic/angular';
import { AppService } from '@services/app/app.service';
import { LabelsService } from '@services/labels/labels.service';
import { HelpService } from '@services/help/help.service';
import { UiService } from '../ui/ui.service';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { ApiErrorDecoder } from '@core/utils';

export interface RetryInstanceModel {
  id: string;
  registeredAt: number;
  notifier$: BehaviorSubject<boolean>;
  techCode?: string;
}

@Injectable({
  providedIn: 'root'
})
export class ErrorService {
  /**
   * configurazione per lo skip della notifica di errore
   */
  private _notificationSkip: NotificationSkipModel;

  /**
   * messaggio di errore
   */
  private message: string;

  /**
   * id tecnico
   */
  private techId: string;

  /**
   * codice tecnico
   */
  private techCode: string;

  /**
   * lista di retry instances
   */
  private retryInstances: RetryInstanceModel[] = [];

  /**
   * accesso in lettura della configurazione per lo skip della notifica di errore
   */
  get notificationSkip(): NotificationSkipModel {
    return this._notificationSkip && { ...this._notificationSkip };
  }

  constructor(
    private toastController: ToastController,
    private modalController: ModalController,
    private appService: AppService,
    private uiService: UiService,
    private labelsService: LabelsService,
    private helpService: HelpService
  ) {}

  /**
   * imposta azione da eseguire sull'eventuale retry.
   * In caso di successo, eseguire sempre il cleanRetryWith
   */
  retryWith(action: () => any, options: { techCode?: string } = {}): string {
    const id = this.createRetryInstance(options);

    this.getRetryWhenOccur(id).subscribe(runAction => {
      if (runAction) {
        action();
      }
    });

    return id;
  }

  /**
   * rimuove l'azione di retry imposta sull'id
   */
  cleanRetryWith(id: string): void {
    this.removeRetryInstance(id);
  }

  /**
   * mostra l'errore partendo dal decoder
   */
  async showErrorFromApi(decoder: ApiErrorDecoder): Promise<void> {
    return this.showError({
      message: decoder.getMessage(),
      techId: decoder.getPartialUuid(),
      techCode: decoder.getCode()
    });
  }

  /**
   * mostra l'errore impostato
   */
  async showError(options: {
    message: string;
    techId?: string;
    techCode?: string;
  }): Promise<void> {
    this.setError(options.message, {
      id: options.techId,
      code: options.techCode
    });

    if (!this.isErrorOccured()) {
      return;
    }

    const mode = this.getErrorDisplayMode();

    if (mode === 'complete') {
      await this.showErrorAsCompleteToast();
    } else if (mode === 'base') {
      await this.showErrorAsBaseToast();
    } else if (mode === 'page') {
      await this.showErrorAsPage();
    }

    this.cleanError();
  }

  /**
   * non mostra il messaggio di errore per la prossima chiamata
   */
  skipNextNotification(options?: { code?: string; url?: string }): void {
    this._notificationSkip = { active: true };

    if (options?.code) {
      this._notificationSkip.code = options.code;
    }

    if (options?.url) {
      this._notificationSkip.url = options.url;
    }
  }

  /**
   * ripristina la configurazione per la visualizzazione dell'errore per le prossime chiamate
   */
  doNextNotification(): void {
    this._notificationSkip = { active: false };
  }

  /**
   * visualizza toast message avanzato con codice errore
   */
  private async showErrorAsCompleteToast(): Promise<void> {
    const toast = await this.toastController.create({
      header: this.labelsService.getLabel(this.message),
      message: `Error code: ${this.techId}`,
      position: 'top',
      color: 'danger',
      duration: 8000,
      cssClass: 'complete-error-toast',
      buttons: [
        {
          icon: 'close',
          role: 'cancel',
          handler: () => toast.dismiss()
        }
      ]
    });

    await toast.present();
  }

  /**
   * visualizza toast message di base
   */
  private async showErrorAsBaseToast(): Promise<void> {
    await this.uiService.showErrorToast(this.message);
  }

  /**
   * visualizza errore in modale dedicata
   */
  private async showErrorAsPage(): Promise<void> {
    const buttons: ErrorButtonModel[] = [];

    if (this.isRetryButtonEnabled()) {
      buttons.push(this.getRetryButton());
    }

    if (this.isAssistanceButtonEnabled()) {
      buttons.push(this.getAssistanceButton());
    }

    const res = await this.uiService.showErrorModal(
      {
        title: this.getErrorTitle(),
        instructions: this.getErrorInstructions(),
        message: this.message,
        techCode: this.techId,
        buttons
      },
      this.modalController
    );

    if (res && res.action === 'retry') {
      this.doRetry();
    } else {
      this.removeRetry();
    }
  }

  /**
   * stabilisce se il retry button è abilitato
   */
  private isRetryButtonEnabled(): boolean {
    return !['exception.hawk.retries.exceeded', 'DGL-IDE-DOC'].includes(
      this.techCode
    );
  }

  private isAssistanceButtonEnabled(): boolean {
    return (
      this.helpService.isLinkActive('live_chat') &&
      ![
        'scan_additional_documents_required',
        'PRI0006_cpi_choice',
        'PRI0006_cpi_calc'
      ].includes(this.techCode)
    );
  }

  /**
   * definisce il bottone di retry
   */
  private getRetryButton(): ErrorButtonModel {
    const baseLabel = 'btn.error_retry';
    const advLabel = 'btn.error_retry_' + this.techCode;

    return {
      text: this.labelsService.doesLabelExists(advLabel) ? advLabel : baseLabel,
      action: 'retry'
    };
  }

  /**
   * definisce il bottone di assistenza
   */
  private getAssistanceButton(): ErrorButtonModel {
    return {
      text: 'btn.error_ask_assistance',
      action: 'stop',
      preventClosing: true,
      handler: () => this.helpService.openLink('live_chat')
    };
  }

  /**
   * modalità di visualizzazione dell'errore
   */
  private getErrorDisplayMode(): ErrorDisplay {
    return this.appService.getConfig().generic.errorDisplay;
  }

  /**
   * restituisce titolo error
   */
  private getErrorTitle(): string {
    const baseLabel = 'wrn.generic_title';
    const advLabel = 'wrn.generic_title_' + this.techCode;

    return this.labelsService.doesLabelExists(advLabel) ? advLabel : baseLabel;
  }

  /**
   * restituisce titolo error
   */
  private getErrorInstructions(): string {
    const baseLabel = 'lbl.what_you_can_do';
    const advLabel = 'lbl.what_you_can_do_' + this.techCode;

    return this.labelsService.doesLabelExists(advLabel) ? advLabel : baseLabel;
  }

  /**
   * stabilisce se è presente un errore
   */
  private isErrorOccured(): boolean {
    return !!this.message;
  }

  /**
   * esegue il retry
   */
  private doRetry(): void {
    const firstRetry = this.getFirstPendingRetryInstance();

    if (!!firstRetry) {
      this.notifyRetryInstance(firstRetry.id);
      this.removeRetryInstance(firstRetry.id);
    }
  }

  /**
   * rimuove il retry
   */
  private removeRetry(): void {
    const firstRetry = this.getFirstPendingRetryInstance();

    if (!!firstRetry) {
      this.notifyRetryInstance(firstRetry.id, false);
      this.removeRetryInstance(firstRetry.id);
    }
  }

  /**
   * restituisce valore quando il retry innescato corrisponde
   * all'id richiesto
   */
  private getRetryWhenOccur(id: string): Observable<boolean> {
    const instance = this.retryInstances.find(x => x.id === id);

    if (!instance) {
      return of(false);
    }

    return instance.notifier$.asObservable().pipe(
      filter(x => x === true),
      take(1)
    );
  }

  /**
   * restituisce la prima instance di retry che risulta pending
   */
  private getFirstPendingRetryInstance(): RetryInstanceModel {
    return this.retryInstances
      .filter(
        x =>
          x.notifier$.getValue() !== true &&
          (!x.techCode || x.techCode === this.techCode)
      )
      .sort((a, b) => a.registeredAt - b.registeredAt)[0];
  }

  /**
   * crea nuovo retry instance
   */
  private createRetryInstance(options: { techCode?: string } = {}): string {
    const now = new Date().getTime();
    const id = `err-${now}`;

    this.retryInstances.push({
      id,
      registeredAt: now,
      notifier$: new BehaviorSubject(false),
      techCode: options.techCode
    });

    return id;
  }

  /**
   * notifica il retry instance
   */
  private notifyRetryInstance(id: string, value: boolean = true): void {
    const instance = this.retryInstances.find(x => x.id === id);

    if (instance) {
      instance.notifier$.next(value);
    }
  }

  /**
   * rimuove il retry instance
   */
  private removeRetryInstance(id: string): void {
    const index = this.retryInstances.findIndex(x => x.id === id);

    if (this.retryInstances[index]) {
      this.retryInstances[index].notifier$.complete();
      this.retryInstances.splice(index, 1);
    }
  }

  /**
   * imposta l'errore
   */
  private setError(
    message: string,
    tech: { id?: string; code?: string } = {}
  ): void {
    this.message = message;
    this.techId = tech.id || null;
    this.techCode = tech.code || null;
  }

  /**
   * cancella l'errore impostato
   */
  private cleanError(): void {
    this.message = undefined;
    this.techId = undefined;
    this.techCode = undefined;
  }
}
